import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Kachel } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import type { Personenangabe } from "@dj/domain/gemeinsam";
import { umschlag } from "../plattform/api";
import type { Zeile } from "../plattform/lesemodelle";
import { einreihen } from "../plattform/warteschlange";
import { navigieren, positionText } from "./geraeteposition";
import { ansprache, symbolDer, useEigenesRevier, useRevierSammlung, type Rahmen } from "./lage";

/**
 * Bergeliste (oberflaeche.md §6, Domänenmodell §13; F-160 … F-165): je Erlegung, die liegt oder zur Bergung markiert ist,
 * ein `article` mit der Ansprache als Titel (§1.5; bei gleicher Ansprache mit „Erlegt durch“), Liegeort, Bemerkung und
 * Erlegungsstatus; „Navigation“ übergibt den Liegeort an die Karten-App, „Liegeort“ ändert einen markierten Liegeort
 * (wie §5 Liegeort; markiert wird ab der Startansicht), „geborgen“ meldet das Stück als geborgen (über die
 * Warteschlange, ADR-0020).
 */
export function Bergeliste({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const revier = useEigenesRevier(id);
  const liste = useRevierSammlung(id, revier, "bergeliste");
  const [gemeldet, setGemeldet] = useState<Set<string>>(new Set());
  const sortiert = [...liste].filter((b) => !gemeldet.has(b._id))
    .sort((a, b) => Number(a["Erlegungsstatus"] === "liegt") - Number(b["Erlegungsstatus"] === "liegt") || String(a["Schuss"]).localeCompare(String(b["Schuss"])));
  const titelVon = (b: Zeile) => {
    const gleich = sortiert.filter((x) => ansprache(x) === ansprache(b)).length > 1;
    const erleger = (b["_person"] as Personenangabe | undefined)?.Name;
    return gleich && erleger ? `${ansprache(b)} ${erleger}` : ansprache(b);
  };

  const geborgen = (b: Zeile) => {
    setGemeldet(new Set(gemeldet).add(b._id));
    void einreihen("AlsGeborgenMelden", `${titelVon(b)} geborgen`, umschlag({ Schuss: b["Schuss"] }));
  };

  const geborgenZahl = liste.filter((b) => b["Erlegungsstatus"] === "geborgen" || gemeldet.has(b._id)).length;
  const zahlen = (
    <>
      <span className="zahl"><b>{liste.length}</b> <span>Stücke</span></span>
      <span className="zahl"><b>{geborgenZahl}</b> <span>geborgen</span></span>
    </>
  );
  const eintrag = (b: Zeile) => {
    const titel = titelVon(b);
    const liegeort = b["Liegeort"] as { Breite: number; Länge: number } | null;
    const titelId = `bergeliste-${b._id}`;
    return (
      <article key={b._id} className="eintrag eintrag--knoepfe eintrag--zeile platte" aria-labelledby={titelId}>
        <Symbol name={symbolDer(String(b["Wildart"] ?? ""), b["Altersklasse"] as string | null)} />
        <h3 className="eintrag__t" id={titelId}>{titel}</h3>
        <span className="eintrag__meta">
          {[String(b["Erlegungsstatus"]), b["Stand"] ? `Stand ${String(b["Stand"])}` : null].filter(Boolean).join(" · ")}
          {" · "}<span className="kennung">{positionText(liegeort)}</span>
          {b["Quelle"] ? ` · ${String(b["Quelle"])}` : null}
          {b["Bemerkung"] ? ` · ${String(b["Bemerkung"])}` : null}
        </span>
        <div className="raster raster--knoepfe">
          {liegeort ? <Kachel symbol="liegeort" begriff="Liegeort" art="kompakt"
            onPress={() => navigate(`/drueckjagd/${id}/liegeort?schuss=${encodeURIComponent(String(b["Schuss"]))}`)} /> : null}
          <Kachel symbol="navigation" begriff="Navigation" art="kompakt" isDisabled={!liegeort} onPress={() => navigieren(liegeort)} />
          <Kachel symbol="bergung" begriff="geborgen" art="kompakt" onPress={() => geborgen(b)} />
        </div>
      </article>
    );
  };
  const [erster, ...uebrige] = sortiert;

  // Liste nach der großen Hauptaktion (design/README §4.4 K71, K76; Referenz `#s-bergeliste`): erster Eintrag über dem
  // Abdruck der Hauptaktion „Bergeliste“, die Zusammenfassung ohne Bedienelement füllt den Abdruck, dann die übrigen.
  return rahmen({
    drueckjagdId: id, name: "Bergeliste", abdruck: true,
    listenkopf: <p className="liste__zus liste__zahlen platte" role="status">{zahlen}</p>,
    children: (
      <section className="liste-bereich" role="region" aria-label="Bergeliste">
        {erster ? eintrag(erster) : null}
        <p className="liste__abdruck liste__zahlen platte" role="status">{zahlen}</p>
        {uebrige.map(eintrag)}
        {sortiert.length === 0 ? <p className="leer platte"><Symbol name="bergung" /><span>Keine Stücke zu bergen</span></p> : null}
      </section>
    ),
  });
}
